import React from 'react';
import { capitalize } from 'lodash';

/**
 * A button for login/register/link/unlink with/from an identity provider.
 *
 * Props:
 * - provider: the name of the provider
 * - method: 'login', 'register', 'link' or 'unlink'
 */
interface IdentityProviderButtonProps {
  /** The name of the identity provider (e.g. 'kuleuven', 'google'). */
  provider: string;
  /** The action this button performs. */
  method: 'login' | 'register' | 'link' | 'unlink';
}

export default function IdentityProviderButton({
  provider,
  method,
}: IdentityProviderButtonProps) {
  /**
   * The text on the button.
   *
   * render 'Central KU Leuven acocount' for kuleuven.
   */
  const buttonText = () => {
    if (method === 'unlink' || method === 'link') {
      return `${capitalize(method)} your ${
        provider === 'kuleuven' ? 'Central KU Leuven' : provider
      } account`;
    } else {
      if (provider === 'kuleuven') {
        return capitalize(method) + ' with your Central KU Leuven account';
      } else {
        return 'Your ' + provider + ' account';
      }
    }
  };

  /**
   * The target for the link.
   *
   * This is always /auth/:provider, except for unlink, where it is
   * /auth/unlink/:provider.
   */
  const linkTarget = () => {
    if (method === 'unlink') {
      return `/auth/unlink/${provider}`;
    } else {
      return `/auth/${provider}`;
    }
  };

  /**
   * Render an a-tag with the correct target and the correct buton text. Also
   * adds a 'btn-<provider>' class on the a-tag to set the logo in css.
   */
  return (
    <a className={`auth-btn btn-${provider}`} href={linkTarget()}>
      {buttonText()}
    </a>
  );
}
